import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    FiCreditCard,
    FiCheck,
    FiZap,
    FiStar,
    FiLoader,
    FiX,
} from "react-icons/fi";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { canAccessPhotoRecolour } from "../../utils/employeeRoles";

import {
    fetchActiveSmilePackages,
    fetchSingleSmilePackage,
} from "../../store/smileDesign/smilePackageSlice";
import { createCheckoutSession } from "../../store/smileDesign/paymentSlice";
import TablePagination from "../../components/smileDesign/TablePagination";
import { CREDIT_SERVICE } from "../../constants/creditService";
import { getCreditUi } from "../../constants/creditPageTheme";

const ListCreditPackages = ({
    service = CREDIT_SERVICE.SMILE,
}: {
    service?: string;
}) => {
    const dispatch = useAppDispatch();

    const {
        activeSmilePackages,
        activePackagesPagination,
        singleSmilePackage,
        loading,
    } = useAppSelector((state) => state.smilePackage);

    const { checkoutLoading, error: paymentError } = useAppSelector(
        (state) => state.payment
    );
    const { user } = useAppSelector((state) => state.auth);
    const { employee } = useAppSelector((state) => state.allocationAuth);
    const isPhotoRecolour = service === CREDIT_SERVICE.PHOTO_RECOLOUR;
    const t = getCreditUi(isPhotoRecolour ? "light" : "dark");
    const creditBalance = isPhotoRecolour
        ? (user?.photo_recolour_credits ?? 0)
        : (user?.smile_credits ?? 0);
    const hasRecolourAccess = canAccessPhotoRecolour(
        user?.role,
        employee?.role,
        user?.verifyPhotoRecolour,
    );

    const [openModal, setOpenModal] = useState(false);
    const [page, setPage] = useState(1);

    const loadPackages = (nextPage = page) => {
        dispatch(fetchActiveSmilePackages({ page: nextPage, service }));
    };

    useEffect(() => {
        loadPackages(1);
    }, []);

    const handleFetchSinglePackage = async (package_id: number) => {
        await dispatch(fetchSingleSmilePackage({ package_id, service }));
        setOpenModal(true);
    };

    const handleBuyPackage = async () => {
        if (!singleSmilePackage) return;

        try {
            const result = await dispatch(
                createCheckoutSession({
                    package_id: singleSmilePackage.package_id,
                    service,
                })
            ).unwrap();

            if (result.checkout_url) {
                window.location.href = result.checkout_url;
            }
        } catch {
            // error shown via paymentError state
        }
    };

    return (
        <div className={t.page}>
            <div className="w-full">
                {isPhotoRecolour && !hasRecolourAccess ? (
                    <div className={`${t.card} p-8 text-center`}>
                        <h1 className={`${t.heading} mb-3`}>Photo Recolour</h1>
                        <p className={t.muted}>
                            This service is not available on your account yet.
                        </p>
                    </div>
                ) : (
                <>
                {user?.role !== "Super Admin" && (
                    <div className="mb-6 max-w-sm">
                        <div className={t.balanceCard}>
                            <div className="flex items-center gap-3">
                                <div className={t.balanceIcon}>
                                    <FiZap className="h-4 w-4" />
                                </div>
                                <div>
                                    <p className={`${t.muted} text-[11px] uppercase tracking-wide`}>
                                        Your Balance
                                    </p>
                                    <p
                                        className={`text-2xl font-bold leading-tight ${
                                            creditBalance <= 0
                                                ? t.danger
                                                : creditBalance <= 5
                                                  ? t.warning
                                                  : t.accent
                                        }`}
                                    >
                                        {creditBalance}{" "}
                                        <span className={`text-sm font-medium ${t.muted}`}>
                                            credits
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mb-8">
                    <div className={`${t.chip} mb-3`}>
                        <FiZap className="h-3.5 w-3.5" />
                        {isPhotoRecolour
                            ? "Photo Recolour Credit Packages"
                            : "Flexible Credit Packages"}
                    </div>

                    <h1 className={t.heading}>Buy Credits</h1>

                    <p className={`${t.subtitle} max-w-2xl`}>
                        {isPhotoRecolour
                            ? "Choose a package for Photo Recolour."
                            : "Choose a credit package that suits your workflow and scale your smile designs effortlessly."}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-4">
                        {isPhotoRecolour && (
                            <Link to="/dashboard/photo-recolour" className={t.link}>
                                Go to Photo Recolour studio
                            </Link>
                        )}
                        <Link
                            to={
                                isPhotoRecolour
                                    ? "/dashboard/photo-recolour/order-history"
                                    : "/dashboard/smiledesign/my-orders"
                            }
                            className={t.link}
                        >
                            View order history
                        </Link>
                    </div>
                </div>

                {loading && activeSmilePackages.length === 0 && (
                    <div className="flex justify-center items-center py-16">
                        <FiLoader className={`${t.loader} text-3xl`} />
                    </div>
                )}

                {paymentError && (
                    <div className={`${t.errorBox} mb-5 px-4 py-3 text-center text-sm`}>
                        {paymentError}
                    </div>
                )}

                {!loading && (!activeSmilePackages || activeSmilePackages.length === 0) && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <FiCreditCard className={`text-4xl ${t.emptyIcon} mb-3`} />
                        <h3 className={`text-lg font-semibold ${t.body} mb-1`}>
                            No Packages Found
                        </h3>
                        <p className={`${t.muted} text-sm max-w-md`}>
                            There are currently no credit packages available. Please check back later.
                        </p>
                    </div>
                )}

                {!loading && activeSmilePackages?.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
                        {activeSmilePackages.map((plan, index) => {
                            const isPopular = page === 1 && index === 1;

                            return (
                                <div
                                    key={plan.package_id}
                                    className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                                        isPopular ? t.cardPopular : t.card
                                    }`}
                                >
                                    {isPopular && (
                                        <div
                                            className={`absolute top-3.5 right-3.5 text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1 ${
                                                isPhotoRecolour
                                                    ? "bg-[#2fa9bf] text-white"
                                                    : "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md"
                                            }`}
                                        >
                                            <FiStar className="text-xs" />
                                            Most Popular
                                        </div>
                                    )}

                                    <div className={t.glowBar} />

                                    <div className="p-6">
                                        <h2 className={`text-2xl font-bold ${t.body}`}>
                                            {plan.credits + (plan.bonus_credits || 0)}
                                            <span className={`${t.accent} text-lg`}>
                                                {" "}Credits
                                            </span>
                                        </h2>

                                        {plan.bonus_credits > 0 && (
                                            <div className={`${t.bonusChip} mt-2`}>
                                                +{plan.bonus_credits} Bonus Credits
                                            </div>
                                        )}

                                        <div className="mt-4 flex items-end gap-2">
                                            <span className={`text-3xl sm:text-4xl font-bold ${t.body}`}>
                                                {plan.currency}
                                                {plan.price}
                                            </span>
                                            <span className={`${t.muted} text-sm mb-1`}>
                                                one-time
                                            </span>
                                        </div>

                                        <p className={`${t.muted} mt-4 text-sm leading-relaxed`}>
                                            {plan.package_name}
                                        </p>

                                        <div className="mt-5 space-y-3">
                                            {[
                                                "Premium rendering support",
                                                "24/7 dashboard access",
                                            ].map((feature, i) => (
                                                <div key={i} className="flex items-center gap-2.5">
                                                    <div className={t.featureIcon}>
                                                        <FiCheck className="text-xs" />
                                                    </div>
                                                    <span className={`${t.body} text-sm`}>
                                                        {feature}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-4">
                                            <span className={plan.isActive ? t.activeChip : t.inactiveChip}>
                                                {plan.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </div>

                                        <button
                                            onClick={() =>
                                                handleFetchSinglePackage(plan.package_id)
                                            }
                                            className={`w-full mt-6 py-3 text-sm ${
                                                isPopular ? t.primaryBtn : t.secondaryBtn
                                            }`}
                                        >
                                            View Package
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <TablePagination
                    pagination={activePackagesPagination}
                    onPageChange={(nextPage) => {
                        setPage(nextPage);
                        loadPackages(nextPage);
                    }}
                    itemLabel="packages"
                    variant={t.pagination}
                />

                {openModal && singleSmilePackage && (
                    <div className={t.modalBackdrop}>
                        <div className={t.modal}>
                            <div className={t.glowBar} />

                            <button
                                onClick={() => setOpenModal(false)}
                                className={`absolute top-3.5 right-3.5 ${t.closeBtn}`}
                            >
                                <FiX className="text-sm" />
                            </button>

                            <div className="p-5 sm:p-6">
                                <div className="pr-9">
                                    <div className={`${t.chip} mb-2.5`}>
                                        <FiStar className="h-3 w-3" />
                                        Selected Package
                                    </div>

                                    <h2 className={`text-xl sm:text-2xl font-bold leading-tight ${t.body}`}>
                                        {singleSmilePackage.package_name}
                                    </h2>

                                    <p className={`${t.muted} mt-1.5 text-sm leading-relaxed`}>
                                        {isPhotoRecolour
                                            ? "Photo Recolour credits."
                                            : "Premium smile design credits."}
                                    </p>
                                </div>

                                <div className="mt-5 grid grid-cols-2 gap-3">
                                    <div className={`${t.innerCard} p-3.5`}>
                                        <p className={`${t.muted} text-xs mb-1.5`}>
                                            Price
                                        </p>
                                        <h3 className={`text-2xl font-bold ${t.body}`}>
                                            {singleSmilePackage.currency}
                                            {singleSmilePackage.price}
                                        </h3>
                                        <p className={`${t.muted} text-xs mt-0.5`}>
                                            One-time
                                        </p>
                                    </div>

                                    <div className={`${t.accentInnerCard} p-3.5`}>
                                        <div className="flex items-center gap-2.5">
                                            <div className={t.balanceIcon}>
                                                <FiCreditCard />
                                            </div>
                                            <div>
                                                <h3 className={`text-xl font-bold leading-none ${t.body}`}>
                                                    {singleSmilePackage.credits +
                                                        (singleSmilePackage.bonus_credits || 0)}
                                                </h3>
                                                <p className={`${t.muted} text-xs mt-1`}>
                                                    Credits
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 space-y-2.5">
                                    {[
                                        "Instant activation",
                                        "Premium support",
                                        "Secure payment",
                                    ].map((feature, index) => (
                                        <div
                                            key={index}
                                            className={`flex items-center gap-2.5 ${t.innerCard} px-3.5 py-2.5`}
                                        >
                                            <div className={t.featureIcon}>
                                                <FiCheck className="text-xs" />
                                            </div>
                                            <span className={`text-sm ${t.body}`}>
                                                {feature}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4">
                                    <span
                                        className={
                                            singleSmilePackage.isActive
                                                ? t.activeChip
                                                : t.inactiveChip
                                        }
                                    >
                                        {singleSmilePackage.isActive
                                            ? "Package Active"
                                            : "Package Inactive"}
                                    </span>
                                </div>

                                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                                    <button
                                        className={`flex-1 py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2 ${t.primaryBtn}`}
                                        onClick={handleBuyPackage}
                                        disabled={checkoutLoading || !singleSmilePackage.isActive}
                                    >
                                        {checkoutLoading ? (
                                            <>
                                                <FiLoader className="animate-spin" />
                                                Processing...
                                            </>
                                        ) : (
                                            "Buy Now"
                                        )}
                                    </button>

                                    <button
                                        onClick={() => setOpenModal(false)}
                                        className={`flex-1 py-3 text-sm ${t.secondaryBtn}`}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-12 text-center">
                    <p className={`${t.muted} text-sm`}>
                        Secure payments • Instant activation • No hidden fees
                    </p>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default ListCreditPackages;
